import { cssVars } from '@lmc/design-system/tokens'
import type { IncidentReport } from '@lmc/ts-common'
import { NAMESPACE } from 'locale'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { INCIDENT_CASE_V2_STEPS } from '../incidentCaseV2Steps'
import ServiceUserDetailsCard from '../ServiceUserDetailsCard'
import { ReadCard } from './readLayout'

type IncidentCaseV2ViewProps = {
  incidentReport: IncidentReport
  testId?: string
}

/**
 * A v2 case, read only: one card per step, in step order, each filled by the
 * step's `ReadPanel` from the registry.
 */
const IncidentCaseV2View = ({
  incidentReport,
  testId = 'incident-case-v2-view'
}: IncidentCaseV2ViewProps) => {
  const { t } = useTranslation(NAMESPACE.INCIDENT_REPORTS)

  return (
    <Body data-testid={testId}>
      <Sections>
        {INCIDENT_CASE_V2_STEPS.map(({ id, titleKey, ReadPanel }) =>
          ReadPanel ? (
            <ReadCard key={id} title={t(titleKey)} testId={`${testId}-${id}`}>
              <ReadPanel incidentReport={incidentReport} />
            </ReadCard>
          ) : null
        )}
      </Sections>
      <Rail>
        <ServiceUserDetailsCard
          testId={`${testId}-service-user`}
          residentId={incidentReport.serviceUser.id}
        />
      </Rail>
    </Body>
  )
}

const { spacing } = cssVars

/**
 * The cards beside the service user's details, as the form lays them out, with
 * the design's 80px under the last card so the page visibly ends.
 */
const Body = styled.div`
  align-items: flex-start;
  display: flex;
  gap: ${spacing[300]};
  padding-bottom: calc(${spacing[800]} + ${spacing[200]});

  @media (max-width: 768px) {
    flex-direction: column;
  }
`

const Sections = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: ${spacing[300]};
  min-width: 0;
`

/** 328px is the frame's rail width; the small frame has no rail. */
const Rail = styled.aside`
  flex-shrink: 0;
  width: 328px;

  @media (max-width: 768px) {
    display: none;
  }
`

export default IncidentCaseV2View
